import type { ProbeTraceStep } from '@terra/shared-contracts-assets';
import type { TraceToolCall } from '../../chat/trace-view/types';

export type ProbeTraceStepPresentation =
  | 'reasoning'
  | 'tool'
  | 'http'
  | 'simple';

const REASONING_TITLE = 'reasoning';
const SEARCH_TITLE = 'search';

const isReasoningStep = (step: ProbeTraceStep): boolean => {
  const title = step.title.trim().toLowerCase();
  const id = step.id.trim().toLowerCase();

  return title === REASONING_TITLE || id.startsWith('reasoning');
};

const isSearchToolStep = (step: ProbeTraceStep): boolean => {
  const title = step.title.trim().toLowerCase();
  const id = step.id.trim().toLowerCase();

  return title === SEARCH_TITLE || id.startsWith('search');
};

export const resolveProbeTraceStepPresentation = ({
  step,
  hasHttpExchange,
}: {
  step: ProbeTraceStep;
  hasHttpExchange: boolean;
}): ProbeTraceStepPresentation => {
  if (isReasoningStep(step)) {
    return 'reasoning';
  }

  if (isSearchToolStep(step)) {
    return 'tool';
  }

  if (hasHttpExchange || Boolean(step.code?.trim())) {
    return 'http';
  }

  return 'simple';
};

export const buildProbeReasoningMarkdown = ({
  step,
}: {
  step: ProbeTraceStep;
}): string => {
  const summary = step.summary?.trim() ?? '';
  const result = step.result?.trim() ?? '';

  if (result && summary && result.startsWith(summary)) {
    return result;
  }

  if (summary && result && summary !== result) {
    return `${summary}\n\n${result}`;
  }

  return result || summary;
};

const toTraceToolStatus = (
  status: ProbeTraceStep['status'],
): TraceToolCall['status'] => {
  if (status === 'FAILED' || status === 'BLOCKED') {
    return 'error';
  }

  if (status === 'PENDING') {
    return 'in-progress';
  }

  return 'completed';
};

export const buildProbeSearchToolCall = ({
  step,
}: {
  step: ProbeTraceStep;
}): TraceToolCall => {
  const question =
    step.summary?.trim() ||
    step.code?.trim() ||
    step.title.trim() ||
    'Search the codebase for this candidate.';
  const output = step.result?.trim() ?? '';

  return {
    id: step.id,
    name: 'ask-codebase',
    args: {
      question,
    },
    status: toTraceToolStatus(step.status),
    output: output || undefined,
    error:
      step.status === 'FAILED' || step.status === 'BLOCKED'
        ? step.summary?.trim() || 'Search did not confirm this candidate.'
        : undefined,
  };
};
